import { Injectable } from "@angular/core";

@Injectable({
  providedIn: "root",
})
export class SessionStorageService {
  constructor() {}

  /**
   * Set a value in session storage.
   * @param key The key to identify the value.
   * @param value The value to be stored. It can be a string or an object.
   */

  /** 
    * how to use :
    saveUserData() {
    const user = { username: 'john.doe', email: 'john.doe@example.com' };
    this.sessionStorageService.setItem('user', user);
    }
   */
  setItem(key: string, value: any): void {
    if (typeof value === "string") {
      sessionStorage.setItem(key, value);
    } else {
      sessionStorage.setItem(key, JSON.stringify(value));
    }
  }

  /**
   * Get a value from session storage.
   * @param key The key to identify the value.
   * @returns The value stored in session storage or null if not found.
   */
  /**
   * how to use :
   loadUserData() {
    const user = this.sessionStorageService.getItem<any>('user');
    if (user) {
      console.log('User:', user);
    } else {
      console.log('No user data found');
    }
  }
  */
  getItem<T>(key: string): T | null {
    const item = sessionStorage.getItem(key);
    try {
      return item ? (JSON.parse(item) as T) : null;
    } catch (e) {
      return item as unknown as T;
    }
  }

  /**
   * Retrieves a specific property from a stored object in session storage.
   * @param storageKey The key to identify the stored object.
   * @param propertyKey The property key to retrieve from the object.
   * @returns The property value, or null if not found.
   */

  /**
   * load spacific storage properties: (in our case like companyId)
   loadUserEmail() {
    const email = this.sessionStorageService.getProperty<string>('user', 'email');
    console.log('User email:', email);
  }
   */
  getProperty<T>(storageKey: string, propertyKey: string): T | null {
    const item = this.getItem<any>(storageKey);
    return item ? item[propertyKey] : null;
  }

  /**
   * Remove a value from session storage.
   * @param key The key to identify the value to be removed.
   */
  /**
   * how to use :
   removeUserData() {
    this.sessionStorageService.removeItem('user');
    console.log('User data removed');
  }
  */
  removeItem(key: string): void {
    sessionStorage.removeItem(key);
  }

  /**
   * Clear all values from session storage.
   */
  /**
   * how to use :
   clearUserData() {
    this.sessionStorageService.clear();
    console.log('All user data removed');
  }
  */
  clear(): void {
    sessionStorage.clear();
  }

  /**
   * Check if a key exists in session storage.
   * @param key The key to check.
   * @returns True if the key exists, otherwise false.
   */
  /**
   * how to use :
   checkUserKey() {
    const exists = this.sessionStorageService.exists('user');
    console.log('User key exists:', exists);
  }
    */
  exists(key: string): boolean {
    return sessionStorage.getItem(key) !== null;
  }
}
